/* eslint-disable react/prop-types */
/* eslint-disable no-use-before-define */
/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from "react";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import sv from "date-fns/locale/sv";
import { setChartDefaults } from "Shared/components/charts/defaults";
import Header from "Shared/navigation/header";
import { NotificationBanners } from "Shared/components/app/notification-banners";
import Main from "Shared/navigation/main";
import Footer from "Shared/navigation/footer";
import Login from "Shared/navigation/login/login";
import { Route, Switch, Redirect } from "react-router-dom";
import {
    getSessionCookie,
    hasValidSessionCookie,
} from "Shared/util/user-session";
import { getLink } from "Shared/navigation/login/routes";
import GlobalContext from "Contexts/global-context";
import useAppContext from "Contexts/app-context";
import { tryTokenRefresh } from "Shared/util/web-api";
import ErrorBoundary from "./error-boundary";
import useUserContext from "Contexts/user-context";
import { useTranslation } from "Shared/util/custom-hooks";

const styles = {
    layout: {
        minHeight: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
    },
};

const setDatePickerLocale = () => {
    registerLocale("sv-SE", sv);
    setDefaultLocale("sv-SE");
};

const TitleFixer = () => {
    const globalTexts = useTranslation((x) => x.global);
    const parts = document.title.split("|");
    document.title = `${parts[0]} | ${globalTexts.title}`;
    return null;
};

const TokenRefresher = ({ children }) => {
    const { appData } = useAppContext();
    const { updateUserSessionToken, invalidateUserSession } = useUserContext();

    useEffect(() => {
        let handle = null;

        const scheduleRefresh = () => {
            let { tokenRefreshIntervalMinutes } = appData;
            if (!tokenRefreshIntervalMinutes) {
                tokenRefreshIntervalMinutes = 1;
            }
            handle = setTimeout(
                refresh,
                tokenRefreshIntervalMinutes * 60 * 1000,
            );
        };

        const refresh = () => {
            const sessionCookie = getSessionCookie();
            if (sessionCookie) {
                tryTokenRefresh().then(({ ok, token }) => {
                    if (ok) {
                        updateUserSessionToken(token);
                        scheduleRefresh();
                    } else {
                        invalidateUserSession();
                    }
                });
            }
        };

        scheduleRefresh();

        return () => {
            if (handle) {
                clearTimeout(handle);
                handle = null;
            }
        };
    });

    return <>{children}</>;
};

const UserPanel = () => (
    <GlobalContext>
        <TokenRefresher>
            <div style={styles.layout}>
                <Header />
                <NotificationBanners />
                <ErrorBoundary>
                    <Main />
                </ErrorBoundary>
                <Footer />
            </div>
        </TokenRefresher>
        <TitleFixer />
    </GlobalContext>
);

const App = () => {
    useEffect(() => {
        setDatePickerLocale();
        setChartDefaults();
    }, []);

    const loginRoute = (path) => (
        <Route path={getLink(path)} render={(props) => <Login {...props} />} />
    );

    const selectOrgRoute = () => (
        <Route
            path={getLink("select-org")}
            render={() => {
                if (!hasValidSessionCookie()) {
                    return <Redirect to={getLink("login")} />;
                }
                return <Redirect to="/" />;
            }}
        />
    );

    const defaultRoute = () => {
        const shouldIgnoreRedirect = () => {
            const sessionExpired = !hasValidSessionCookie();
            // NOTE: Used to be able to manually retrieve the activation code from Fortnox callback when not signed in to My pages.
            const isFortnoxCallback =
                sessionExpired &&
                window.location.pathname === "/sales-methods/fortnox-setup" &&
                new URLSearchParams(window.location.search)?.get("code") !==
                    null;

            if (isFortnoxCallback) {
                return true;
            }
            return false;
        };

        return (
            <Route
                path="/"
                render={() => {
                    if (shouldIgnoreRedirect()) {
                        return null;
                    } else if (!hasValidSessionCookie()) {
                        return <Redirect to={getLink("select-org")} />;
                    } else {
                        return <UserPanel />;
                    }
                }}
            />
        );
    };

    return (
        <Switch>
            {loginRoute("login")}
            {loginRoute("activate-account")}
            {loginRoute("change-password")}
            {loginRoute("forgot-password")}
            {loginRoute("update-account")}
            {selectOrgRoute()}
            {defaultRoute()}
        </Switch>
    );
};

export default App;
